import React, { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { billingAPI } from "../../services/api/billing";
import { LOW_CREDIT_FLOOR } from "../../hooks/useEntitlements";

function Banner({ type, message, action, onDismiss }) {
  const styles = {
    info:    { wrapper: "bg-blue-50 border-blue-100 text-blue-800",    icon: Info,          iconCls: "text-blue-500" },
    warning: { wrapper: "bg-amber-50 border-amber-100 text-amber-800", icon: AlertTriangle,  iconCls: "text-amber-500" },
    error:   { wrapper: "bg-red-50 border-red-100 text-red-800",       icon: AlertCircle,   iconCls: "text-red-500" },
  };
  const s = styles[type] || styles.info;
  const Icon = s.icon;
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b text-sm ${s.wrapper}`}>
      <Icon className={`h-4 w-4 flex-shrink-0 ${s.iconCls}`} />
      <span className="flex-1">{message}</span>
      {action && (
        <a
          href={action.href}
          {...(action.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="font-medium underline underline-offset-2 whitespace-nowrap"
        >
          {action.label}
        </a>
      )}
      {onDismiss && (
        <button onClick={onDismiss} className="p-0.5 rounded hover:bg-black/5 transition-colors">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function BillingBanner() {
  const { user, isAuthenticated, isInitialized, currentWorkspace } = useAuth();
  const [sub, setSub] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Scope to the CURRENTLY VIEWED workspace and refetch on switch — billing
  // belongs to the workspace, not the user. Without client_id the banner would
  // always reflect the default workspace (wrong for admins / after a switch).
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) return;
    let cancelled = false;
    const wsId = currentWorkspace?.id;
    setDismissed(false); // a fresh workspace's warning shouldn't stay dismissed
    billingAPI.getCurrentSubscription(wsId)
      .then(d => { if (!cancelled) setSub(d); })
      .catch(() => {});
    billingAPI.getBillingSummary(wsId)
      .then(d => { if (!cancelled) setSummary(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isInitialized, isAuthenticated, user, currentWorkspace?.id]);

  if (!isInitialized || !isAuthenticated || !user || !sub || dismissed) return null;

  if (summary?.read_only) {
    const reasonMsg = {
      subscription_inactive: "Your subscription is inactive.",
    }[summary.read_only_reason] || "Your workspace is paused.";

    if (summary.can_manage_billing) {
      return (
        <Banner
          type="error"
          message={`${reasonMsg} Add a payment method to reactivate this workspace.`}
          action={{ href: "/settings/billing", label: "Manage billing" }}
        />
      );
    }
    return (
      <Banner
        type="error"
        message={`${reasonMsg} Ask an owner or admin to add a payment method to reactivate.`}
      />
    );
  }

  // Only urgent billing states get a banner below; routine plan/credit state
  // is surfaced elsewhere (e.g. CurrentPlanCard on the billing settings page).
  if (sub.status === "past_due") {
    const issue = sub.payment_issue;
    if (issue?.kind === "action_required" && issue.invoice_url) {
      return (
        <Banner
          type="error"
          message="Your bank needs you to confirm your latest payment. Your card wasn't declined."
          action={{ href: issue.invoice_url, label: "Confirm payment", external: true }}
        />
      );
    }
    return (
      <Banner
        type="error"
        message="Your last payment failed."
        action={{ href: "/settings/billing", label: "Update card" }}
      />
    );
  }
  if (sub.cancel_at_period_end) {
    if (sub.pending_plan?.code === "pay_as_you_go") {
      return (
        <Banner
          type="warning"
          message={`Switching to Pay as you go on ${new Date(sub.current_period_end).toLocaleDateString()}.`}
          action={{ href: "/settings/billing", label: "View" }}
        />
      );
    }
    return (
      <Banner
        type="warning"
        message={`Subscription canceling on ${new Date(sub.current_period_end).toLocaleDateString()}.`}
        action={{ href: "/settings/billing", label: "Reactivate" }}
      />
    );
  }
  const isPayg = sub.plan?.code === "pay_as_you_go";
  if (summary && (isPayg || sub.plan?.monthly_credits > 0)) {
    const balance = Number(summary.total_balance ?? 0);
    if (balance <= 0) {
      return (
        <Banner
          type="error"
          message="You're out of credits. New runs are paused."
          action={{ href: "/settings/billing?buy=1", label: "Add credits" }}
        />
      );
    }
    const lowForPayg = isPayg && balance < LOW_CREDIT_FLOOR;
    const lowForGrowth = !isPayg && (() => {
      const allocation = summary.plan_period_allocation || sub.plan.monthly_credits;
      const ratio = allocation > 0 ? balance / allocation : 1;
      return ratio < 0.2;
    })();
    if (lowForPayg || lowForGrowth) {
      return (
        <Banner
          type="warning"
          message="You're almost out of credits."
          action={{ href: "/settings/billing?buy=1", label: "Add credits" }}
          onDismiss={() => setDismissed(true)}
        />
      );
    }
  }
  if (sub.status === "canceled") {
    return (
      <Banner
        type="warning"
        message="Your account is read-only."
        action={{ href: "/settings/billing", label: "Reactivate" }}
      />
    );
  }
  return null;
}
